<template>
  <div class="auth-form-container">
    <h2>Rekisteröidy</h2>
    <form @submit.prevent="signUp" novalidate>
      <div class="input-group">
        <input 
          v-model="username" 
          placeholder="Käyttäjänimi/Raviremmisi nimi" 
          type="text"
          maxlength="50"
          :class="{ 'input-error': validationErrors.username }"
          @blur="validateUsername"
          @input="clearFieldError('username')"
          required 
        />
        <div class="input-hint">
          <span class="public-indicator">🌐 Julkinen</span> - Näkyy muille pelaajille tulostaulukoissa
        </div>
        <div class="input-requirements">
          3-50 merkkiä, kirjaimet a-z, ä, ö, å, numerot, väliviiva ja alaviiva
        </div>
        <div v-if="validationErrors.username" class="field-error">{{ validationErrors.username }}</div>
      </div>
      
      <div class="input-group">
        <input 
          v-model="firstName" 
          placeholder="Etunimi" 
          type="text"
          maxlength="50"
          :class="{ 'input-error': validationErrors.firstName }"
          @blur="validateFirstName"
          @input="clearFieldError('firstName')"
          required 
        />
        <div class="input-hint">
          <span class="private-indicator">🔒 Yksityinen</span> - Vain sinun nähtävilläsi
        </div>
        <div v-if="validationErrors.firstName" class="field-error">{{ validationErrors.firstName }}</div>
      </div>
      
      <div class="input-group">
        <input 
          v-model="lastName" 
          placeholder="Sukunimi" 
          type="text"
          maxlength="50"
          :class="{ 'input-error': validationErrors.lastName }"
          @blur="validateLastName"
          @input="clearFieldError('lastName')"
          required 
        />
        <div class="input-hint">
          <span class="private-indicator">🔒 Yksityinen</span> - Vain sinun nähtävilläsi
        </div>
        <div v-if="validationErrors.lastName" class="field-error">{{ validationErrors.lastName }}</div>
      </div>
      
      <div class="input-group">
        <input 
          v-model="email" 
          placeholder="Sähköposti" 
          type="email" 
          maxlength="254"
          :class="{ 'input-error': validationErrors.email }"
          @blur="validateEmail"
          @input="clearFieldError('email')"
          required 
        />
        <div class="input-hint">
          <span class="private-indicator">🔒 Yksityinen</span> - Vain sinun nähtävilläsi
        </div>
        <div v-if="validationErrors.email" class="field-error">{{ validationErrors.email }}</div>
      </div>
      
      <div class="input-group">
        <input 
          v-model="password" 
          placeholder="Salasana" 
          type="password" 
          maxlength="128"
          :class="{ 'input-error': validationErrors.password }"
          @blur="validatePassword"
          @input="clearFieldError('password')"
          required 
        />
        <div class="password-requirements">
          <div class="requirements-title">Salasanan vaatimukset:</div>
          <ul class="requirements-list">
            <li :class="{ 'requirement-met': passwordChecks.length }">
              <span class="check-icon">{{ passwordChecks.length ? '✓' : '✗' }}</span>
              Vähintään 8 merkkiä
            </li>
            <li :class="{ 'requirement-met': passwordChecks.lowercase }">
              <span class="check-icon">{{ passwordChecks.lowercase ? '✓' : '✗' }}</span>
              Pieni kirjain (a-z)
            </li>
            <li :class="{ 'requirement-met': passwordChecks.uppercase }">
              <span class="check-icon">{{ passwordChecks.uppercase ? '✓' : '✗' }}</span>
              Iso kirjain (A-Z)
            </li>
            <li :class="{ 'requirement-met': passwordChecks.digit }">
              <span class="check-icon">{{ passwordChecks.digit ? '✓' : '✗' }}</span>
              Numero (0-9)
            </li>
          </ul>
        </div>
        <div v-if="validationErrors.password" class="field-error">{{ validationErrors.password }}</div>
      </div>
      
      <div class="input-group">
        <input 
          v-model="confirmPassword" 
          placeholder="Vahvista salasana" 
          type="password" 
          maxlength="128"
          :class="{ 'input-error': validationErrors.confirmPassword }"
          @blur="validateConfirmPassword"
          @input="clearFieldError('confirmPassword')"
          required 
        />
        <div v-if="validationErrors.confirmPassword" class="field-error">{{ validationErrors.confirmPassword }}</div>
      </div>
      
      <div class="terms-acceptance">
        <label class="checkbox-container">
          <input 
            type="checkbox" 
            v-model="termsAccepted" 
            class="terms-checkbox"
            required
          />
          <span class="checkmark"></span>
          <span class="checkbox-text">
            Hyväksyn 
            <button type="button" @click="showTermsModal = true" class="terms-link">käyttöehdot</button>
          </span>
        </label>
      </div>
      
      <button type="submit" :disabled="!isFormValid || isLoading">
        {{ isLoading ? 'Rekisteröidään...' : 'Rekisteröidy' }}
      </button>
    </form>
    <div v-if="googleSignInAvailable" class="google-auth-section">
      <div class="google-auth-divider"><span>tai</span></div>
      <button
        type="button"
        class="google-sign-in-button"
        aria-describedby="google-sign-up-help"
        :disabled="isGoogleLoading"
        @click="signInWithGoogle"
      >
        <span class="google-mark" aria-hidden="true">G</span>
        {{ isGoogleLoading ? 'Siirrytään Googleen...' : 'Rekisteröidy Googlella' }}
      </button>
      <div id="google-sign-up-help" class="google-auth-note" role="note">
        <p>
          Googlella rekisteröityessäsi valitset seuraavaksi remmin nimen ja hyväksyt käyttöehdot.
        </p>
        <p>
          Jos vahvistettu Google-sähköpostisi vastaa olemassa olevaa Raviremmi-tiliä, uutta tiliä ei
          luoda, vaan Google-kirjautuminen liitetään siihen. Salasanakirjautuminen säilyy käytössä.
        </p>
        <p>
          Raviremmi käyttää vain perustunnistautumistietoja eikä pyydä Googlelta Gmail- tai
          Drive-oikeuksia.
        </p>
      </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div class="auth-link">
       Onko sinulla jo tili? <router-link to="/sign-in">Kirjaudu sisään</router-link>
    </div>
    
    <!-- Terms Modal -->
    <TermsModal :show="showTermsModal" @close="showTermsModal = false" />
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { Auth } from 'aws-amplify';
import TermsModal from '../TermsModal.vue';

export default {
  name: 'SignUp',
  components: {
    TermsModal
  },
  setup() {
    const username = ref('');
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const termsAccepted = ref(false);
    const showTermsModal = ref(false);
    const error = ref('');
    const isLoading = ref(false);
    const isGoogleLoading = ref(false);
    const googleSignInAvailable = process.env.VUE_APP_GOOGLE_AUTH_ENABLED === 'true';
    const router = useRouter();
    
    // Validation errors object
    const validationErrors = reactive({
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    });

    // Input sanitization function
    const sanitizeInput = (input, maxLength = 255) => {
      if (typeof input !== 'string') return '';
      
      // Remove dangerous characters by filtering each character
      let sanitized = '';
      for (let i = 0; i < input.length; i++) {
        const char = input[i];
        const charCode = char.charCodeAt(0);
        
        // Allow printable characters, space, tab, and newline
        // Skip control characters (0-31 except 9,10) and DEL (127)
        if (charCode >= 32 || charCode === 9 || charCode === 10) {
          sanitized += char;
        }
      }
      
      // Trim whitespace
      sanitized = sanitized.trim();
      
      // Limit length
      if (sanitized.length > maxLength) {
        sanitized = sanitized.substring(0, maxLength);
      }
      
      return sanitized;
    };

    // Username validation - more relaxed
    const validateUsername = () => {
      const sanitized = sanitizeInput(username.value, 50);
      username.value = sanitized;
      
      // Only validate if user has actually typed something
      if (!sanitized && username.value.length === 0) {
        validationErrors.username = '';
        return false; // Invalid but no error message
      }
      
      if (sanitized.length > 0 && sanitized.length < 3) {
        validationErrors.username = 'Käyttäjänimen tulee olla vähintään 3 merkkiä pitkä';
        return false;
      }
      
      if (sanitized.length > 50) {
        validationErrors.username = 'Käyttäjänimi voi olla enintään 50 merkkiä pitkä';
        return false;
      }
      
      // Only check format if they've typed something substantial
      if (sanitized.length >= 3) {
        // Allow Finnish characters ä, ö, å (both upper and lowercase)
        const usernameRegex = /^[a-zA-ZäöåÄÖÅ0-9_-]+$/;
        if (!usernameRegex.test(sanitized)) {
          validationErrors.username = 'Käyttäjänimi saa sisältää vain kirjaimia (a-z, ä, ö, å), numeroita, väliviivoja ja alaviivoja';
          return false;
        }
        
        if (/[-_]{2,}/.test(sanitized)) {
          validationErrors.username = 'Käyttäjänimi ei saa sisältää peräkkäisiä erikoismerkkejä';
          return false;
        }
        
        if (!/^[a-zA-Z0-9].*[a-zA-Z0-9]$/.test(sanitized) && sanitized.length > 1) {
          validationErrors.username = 'Käyttäjänimen tulee alkaa ja päättyä kirjaimeen tai numeroon';
          return false;
        }
      }
      
      validationErrors.username = '';
      return sanitized.length >= 3;
    };

    // Name validation (for first and last names) - more relaxed
    const validateName = (name, fieldName, errorField) => {
      const sanitized = sanitizeInput(name, 50);
      
      // Don't show "required" error for empty fields
      if (!sanitized) {
        validationErrors[errorField] = '';
        return { isValid: false, sanitized };
      }
      
      if (sanitized.length > 50) {
        validationErrors[errorField] = `${fieldName} voi olla enintään 50 merkkiä pitkä`;
        return { isValid: false, sanitized };
      }
      
      // Allow letters, spaces, hyphens, and common accented characters
      const nameRegex = /^[a-zA-ZäöåÄÖÅ\s-]+$/;
      if (!nameRegex.test(sanitized)) {
        validationErrors[errorField] = `${fieldName} saa sisältää vain kirjaimia, välilyöntejä ja väliviivoja`;
        return { isValid: false, sanitized };
      }
      
      // Check for excessive spaces or hyphens
      if (/\s{2,}/.test(sanitized) || /--+/.test(sanitized)) {
        validationErrors[errorField] = `${fieldName} ei saa sisältää peräkkäisiä välilyöntejä tai väliviivoja`;
        return { isValid: false, sanitized };
      }
      
      // Must not start or end with space or hyphen
      if (/^[\s-]|[\s-]$/.test(sanitized)) {
        validationErrors[errorField] = `${fieldName} ei saa alkaa tai päättyä välilyöntiin tai väliviivaan`;
        return { isValid: false, sanitized };
      }
      
      validationErrors[errorField] = '';
      return { isValid: true, sanitized };
    };

    const validateFirstName = () => {
      const result = validateName(firstName.value, 'Etunimi', 'firstName');
      firstName.value = result.sanitized;
      return result.isValid;
    };

    const validateLastName = () => {
      const result = validateName(lastName.value, 'Sukunimi', 'lastName');
      lastName.value = result.sanitized;
      return result.isValid;
    };

    // Email validation - more relaxed
    const validateEmail = () => {
      const sanitized = sanitizeInput(email.value, 254);
      email.value = sanitized;
      
      // Don't show error for empty email
      if (!sanitized) {
        validationErrors.email = '';
        return false;
      }
      
      if (sanitized.length > 254) {
        validationErrors.email = 'Sähköpostiosoite on liian pitkä';
        return false;
      }
      
      // Basic email validation
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(sanitized)) {
        validationErrors.email = 'Syötä kelvollinen sähköpostiosoite';
        return false;
      }
      
      // Check for suspicious patterns
      if (sanitized.includes('..') || sanitized.startsWith('.') || sanitized.endsWith('.')) {
        validationErrors.email = 'Sähköpostiosoite sisältää virheellisiä merkkejä';
        return false;
      }
      
      validationErrors.email = '';
      return true;
    };

    // Password validation
    const validatePassword = () => {
      const pwd = password.value;
      
      if (!pwd) {
        validationErrors.password = 'Salasana on pakollinen';
        return false;
      }
      
      if (pwd.length > 128) {
        validationErrors.password = 'Salasana voi olla enintään 128 merkkiä pitkä';
        return false;
      }
      
      // Check for problematic characters using character code checking
      for (let i = 0; i < pwd.length; i++) {
        const charCode = pwd.charCodeAt(i);
        // Check for control characters (0-31 except tab/newline) and DEL (127)
        if ((charCode >= 0 && charCode <= 31 && charCode !== 9 && charCode !== 10) || charCode === 127) {
          validationErrors.password = 'Salasana sisältää virheellisiä merkkejä';
          return false;
        }
      }
      
      const errors = [];
      
      if (pwd.length < 8) {
        errors.push('vähintään 8 merkkiä');
      }
      
      if (!/[a-z]/.test(pwd)) {
        errors.push('pieni kirjain');
      }
      
      if (!/[A-Z]/.test(pwd)) {
        errors.push('iso kirjain');
      }
      
      if (!/[0-9]/.test(pwd)) {
        errors.push('numero');
      }
      
      if (errors.length > 0) {
        validationErrors.password = `Salasana tarvitsee: ${errors.join(', ')}`;
        return false;
      }
      
      validationErrors.password = '';
      return true;
    };

    // Confirm password validation - more relaxed
    const validateConfirmPassword = () => {
      if (!confirmPassword.value) {
        validationErrors.confirmPassword = '';
        return false;
      }
      
      if (confirmPassword.value.length > 128) {
        validationErrors.confirmPassword = 'Salasanan vahvistus on liian pitkä';
        return false;
      }
      
      if (password.value !== confirmPassword.value) {
        validationErrors.confirmPassword = 'Salasanat eivät täsmää';
        return false;
      }
      
      validationErrors.confirmPassword = '';
      return true;
    };

    // Clear field error
    const clearFieldError = (field) => {
      validationErrors[field] = '';
    };

    // Password strength checks for visual feedback
    const passwordChecks = computed(() => ({
      length: password.value.length >= 8,
      lowercase: /[a-z]/.test(password.value),
      uppercase: /[A-Z]/.test(password.value),
      digit: /[0-9]/.test(password.value)
    }));

    // Form validation
    const isFormValid = computed(() => {
      return (
        username.value.length >= 3 &&
        firstName.value.length > 0 &&
        lastName.value.length > 0 &&
        email.value.length > 0 &&
        passwordChecks.value.length &&
        passwordChecks.value.lowercase &&
        passwordChecks.value.uppercase &&
        passwordChecks.value.digit &&
        password.value === confirmPassword.value &&
        termsAccepted.value &&
        Object.values(validationErrors).every(error => error === '')
      );
    });

    const signUp = async () => {
      error.value = '';
      
      // Validate all fields
      const isUsernameValid = validateUsername();
      const isFirstNameValid = validateFirstName();
      const isLastNameValid = validateLastName();
      const isEmailValid = validateEmail();
      const isPasswordValid = validatePassword();
      const isConfirmPasswordValid = validateConfirmPassword();
      
      if (!isUsernameValid || !isFirstNameValid || !isLastNameValid || 
          !isEmailValid || !isPasswordValid || !isConfirmPasswordValid) {
        error.value = 'Korjaa virheet ennen jatkamista';
        return;
      }
      
      if (!termsAccepted.value) {
        error.value = 'Sinun tulee hyväksyä käyttöehdot rekisteröityäksesi';
        return;
      }
      
      isLoading.value = true;
      
      try {
        await Auth.signUp({
          username: username.value,
          password: password.value,
          attributes: {
            email: email.value,
            given_name: firstName.value,
            family_name: lastName.value,
            preferred_username: username.value,
          },
        });
        
        // Store username for confirmation page
        try {
          sessionStorage.setItem('usernameForConfirmation', username.value);
        } catch (storageError) {
          console.warn("Could not store username in sessionStorage:", storageError);
        }
        
        router.push({ path: '/confirm-sign-up', query: { username: username.value } });
      } catch (err) {
        console.error('Sign up error:', err);
        
        // Enhanced error handling
        if (err.code === 'AliasExistsException' || err.message.includes('already exists')) {
          error.value = 'Tällä sähköpostiosoitteella on jo olemassa tili.';
        } else if (err.code === 'UsernameExistsException') {
          error.value = 'Tämä käyttäjänimi on jo käytössä. Valitse toinen käyttäjänimi.';
          validationErrors.username = 'Käyttäjänimi on jo käytössä';
        } else if (err.code === 'InvalidPasswordException') {
          error.value = 'Salasana ei täytä vaatimuksia. Tarkista salasanan vaatimukset.';
          validationErrors.password = 'Salasana ei täytä vaatimuksia';
        } else if (err.code === 'InvalidParameterException') {
          if (err.message.includes('email')) {
            error.value = 'Sähköpostiosoite on virheellinen.';
            validationErrors.email = 'Virheellinen sähköpostiosoite';
          } else if (err.message.includes('username')) {
            error.value = 'Käyttäjänimi on virheellinen.';
            validationErrors.username = 'Virheellinen käyttäjänimi';
          } else {
            error.value = 'Jokin syötetyistä tiedoista on virheellinen.';
          }
        } else if (err.code === 'PreSignUpTriggerException') {
          error.value = err.message || 'Rekisteröityminen epäonnistui. Tarkista tiedot ja yritä uudelleen.';
        } else if (err.code === 'TooManyRequestsException') {
          error.value = 'Liian monta yritystä. Odota hetki ja yritä uudelleen.';
        } else if (err.code === 'LimitExceededException') {
          error.value = 'Rekisteröitymisraja ylitetty. Yritä myöhemmin uudelleen.';
        } else {
          error.value = err.message || 'Rekisteröitymisen aikana tapahtui odottamaton virhe.';
        }
      } finally {
        isLoading.value = false;
      }
    };

    const signInWithGoogle = async () => {
      error.value = '';
      isGoogleLoading.value = true;
      try {
        await Auth.federatedSignIn({ provider: 'Google' });
      } catch (err) {
        console.error('Google sign-in error:', err);
        error.value = err.message || 'Google-kirjautumisen aloittaminen epäonnistui.';
        isGoogleLoading.value = false;
      }
    };

    return {
      username,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      termsAccepted,
      showTermsModal,
      error,
      isLoading,
      isGoogleLoading,
      googleSignInAvailable,
      validationErrors,
      passwordChecks,
      isFormValid,
      validateUsername,
      validateFirstName,
      validateLastName,
      validateEmail,
      validatePassword,
      validateConfirmPassword,
      clearFieldError,
      signUp,
      signInWithGoogle,
    };
  },
};
</script>

<style scoped>
/* Styles for the form container */
.auth-form-container {
  max-width: 450px;
  margin: 30px auto;
  padding: 30px 40px;
  background-color: #112240;
  border-radius: 8px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
}

/* Heading style */
.auth-form-container h2 {
  color: #ffffff;
  text-align: center;
  margin-bottom: 25px;
  font-size: 1.8rem;
  font-weight: 500;
}

/* Form layout */
.auth-form-container form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Input group for fields with privacy hints */
.input-group {
  display: flex;
  flex-direction: column;
}

.input-hint {
  font-size: 0.8rem;
  color: #8892b0;
  margin-top: 5px;
  font-style: italic;
}

.input-requirements {
  font-size: 0.75rem;
  color: #64ffda;
  margin-top: 3px;
  opacity: 0.8;
}

.public-indicator {
  color: #64ffda;
  font-weight: 600;
}

.private-indicator {
  color: #a8b2d1;
  font-weight: 600;
}

/* --- CORRECTED INPUT STYLES --- */
/* Target common text/password inputs AND inputs without a type (defaulting to text) */
.auth-form-container input[type="text"],
.auth-form-container input[type="email"],
.auth-form-container input[type="password"],
.auth-form-container input:not([type]) { /* Added this selector */
  background-color: #0a192f;
  border: 1px solid #1e3a5f;
  color: #ccd6f6;
  padding: 12px 15px;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

/* Input error state */
.auth-form-container input.input-error {
  border-color: #ff6b6b;
  box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.2);
}

/* --- CORRECTED INPUT FOCUS STYLES --- */
.auth-form-container input[type="text"]:focus,
.auth-form-container input[type="email"]:focus,
.auth-form-container input[type="password"]:focus,
.auth-form-container input:not([type]):focus { /* Added this selector */
  outline: none;
  border-color: #64ffda; /* Teal border */
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15); /* Teal glow */
}

.auth-form-container input.input-error:focus {
  border-color: #ff6b6b;
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.15);
}

/* Placeholder text style (applies to all inputs) */
.auth-form-container input::placeholder {
  color: #8892b0;
  opacity: 0.7;
}

/* Field error messages */
.field-error {
  color: #ff6b6b;
  font-size: 0.8rem;
  margin-top: 5px;
  padding: 5px 8px;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 4px;
  border-left: 3px solid #ff6b6b;
}

/* Password requirements styling */
.password-requirements {
  margin-top: 8px;
  padding: 12px;
  background-color: rgba(100, 255, 218, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(100, 255, 218, 0.1);
}

.requirements-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6f1ff;
  margin-bottom: 8px;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.requirements-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #8892b0;
  transition: color 0.3s ease;
}

.requirements-list li.requirement-met {
  color: #64ffda;
}

.check-icon {
  font-size: 0.75rem;
  font-weight: bold;
  width: 12px;
  text-align: center;
}

.requirements-list li.requirement-met .check-icon {
  color: #64ffda;
}

.requirements-list li:not(.requirement-met) .check-icon {
  color: #ff6b6b;
}

/* Submit button style */
.auth-form-container button[type="submit"] {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, opacity 0.3s ease;
  margin-top: 10px;
}

.auth-form-container button[type="submit"]:hover:not(:disabled) {
  background-color: #52d6c0;
  transform: translateY(-1px);
}

.auth-form-container button[type="submit"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Error message style */
.error-message {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 20px;
}

/* Link style */
.auth-link {
    text-align: center;
    margin-top: 25px;
    font-size: 0.9rem;
    color: #a8b2d1;
}

.auth-link a {
    color: #64ffda;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
}

.auth-link a:hover {
  color: #ffffff;
  text-decoration: underline;
}

.google-auth-section {
  margin-top: 22px;
}

.google-auth-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #8892b0;
  font-size: 0.82rem;
  margin-bottom: 14px;
}

.google-auth-divider::before,
.google-auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #1e3a5f;
}

.google-sign-in-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 11px 16px;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
}

.google-sign-in-button:hover:not(:disabled) {
  background: #ffffff;
  border-color: #94a3b8;
}

.google-sign-in-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.google-mark {
  display: inline-grid;
  width: 20px;
  height: 20px;
  place-items: center;
  color: #4285f4;
  font-family: Georgia, serif;
  font-size: 1.08rem;
  font-weight: 700;
}

.google-auth-note {
  margin: 10px 0 0;
  color: #8892b0;
  font-size: 0.8rem;
  line-height: 1.45;
}

.google-auth-note p {
  margin: 0;
}

.google-auth-note p + p {
  margin-top: 8px;
}

/* Terms acceptance styling */
.terms-acceptance {
  margin: 1rem 0;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  line-height: 1.4;
}

.terms-checkbox {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #1e3a5f;
  border-radius: 3px;
  background-color: #0a192f;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.3s ease;
}

.terms-checkbox:checked {
  background-color: #64ffda;
  border-color: #64ffda;
}

.terms-checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #0a192f;
  font-size: 12px;
  font-weight: bold;
}

.terms-checkbox:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15);
}

.checkbox-text {
  color: #a8b2d1;
  font-size: 0.9rem;
  flex: 1;
}

.terms-link {
  background: none;
  border: none;
  color: #64ffda;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  transition: color 0.3s ease;
}

.terms-link:hover {
  color: #ffffff;
}

.terms-link:focus {
  outline: 2px solid #64ffda;
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .auth-form-container {
    margin: 20px auto;
    padding: 20px 25px;
  }
  
  .requirements-list {
    font-size: 0.75rem;
  }
  
  .password-requirements {
    padding: 10px;
  }
}
</style>
